import React, {useState} from 'react';
import {Box, ButtonBase, Collapse} from '@mui/material';
import {Typography} from '@mui/joy';
import {useQuery} from '@apollo/client';
import {useTranslation} from 'react-i18next';
import _ from 'lodash';
import {BuildRounded, KeyboardArrowDownRounded} from '@mui-symbols-material/w300';
import {WidgetBox} from '@dreebit-registry/vsm-microservice-components';
import {SkeletonListRows, fadeInSx} from './HeroSkeletons';
import {HeroCardHeader} from './heroPrimitives';
import HeroEntity from './HeroEntity';
import HeroContactList, {HeroContact} from './HeroContactList';
import {heroSectionSx} from './heroCardStyles';
import ContactChannelLinks from '../ContactChannelLinks';
import {InstalledBaseNodeServiceUnit} from '../../graphql/queries/installedBaseNodeServiceUnit';
import {getServiceUnitSourceInfo} from '../../utils/serviceUnitSource';
import useOwnClient from '../../hooks/useOwnClient';
import useNodeContactTabLink from '../../hooks/useNodeContactTabLink';

// Beyond this the card summarises ("+ N more contacts") — the full list lives
// on the node's contact tab.
const MAX_CONTACTS = 2;

interface Props {
    /** Node to resolve against — the asset's installedBaseNodeId, or the systemId (systems are nodes). */
    nodeId: string;
}

/**
 * Service center card for the asset/system dashboards: who serves this machine
 * (IBM-972). Shown to everyone the backend resolves a unit for, like the node
 * contact tab — only the wording follows the viewer's perspective (identities
 * without a company, i.e. global admins, get the customer wording). Same quiet
 * anatomy as HeroCustomerCard: name, grey source line (a fallback explains
 * itself behind an info icon), hotline, named contacts. For internal viewers
 * the named contacts start collapsed: they look for the customer's contact,
 * which the customer card shows (IBM-1086).
 */
const HeroServiceUnitCard = ({nodeId}: Props) => {
    const {t} = useTranslation();
    const ownClient = useOwnClient();
    const perspective = ownClient?.isServiceProvider ? 'provider' : 'customer';
    const [contactsOpen, setContactsOpen] = useState(false);

    const {data, loading} = useQuery(InstalledBaseNodeServiceUnit, {
        variables: {nodeId},
        skip: !nodeId,
    });
    const contactTab = useNodeContactTabLink(nodeId);

    const resolved = _.get(data, 'installedBaseNode.serviceUnit', null);
    const unit = resolved?.unit;
    const sourceInfo = getServiceUnitSourceInfo(resolved?.source, t, {
        ancestorTitle: resolved?.inheritedFrom?.title,
        unitType: unit?.type,
        perspective,
    });
    const title = resolved && !sourceInfo.isFallback ? sourceInfo.title : t('Central service contact');

    // Null without data = no read access or unconfigured environment — in both
    // cases the card adds nothing, so it disappears rather than showing a gap.
    if (!loading && !unit) return null;

    const serviceContacts: HeroContact[] = (unit?.serviceContacts ?? []).filter(Boolean);
    const collapsible = perspective === 'provider';
    const contactList = (
        <HeroContactList
            contacts={serviceContacts.slice(0, MAX_CONTACTS)}
            hiddenCount={Math.max(0, serviceContacts.length - MAX_CONTACTS)}
            moreTo={contactTab.url}
            contactTestId="service-unit-contact"
        />
    );

    return (
        <WidgetBox>
            <HeroCardHeader title={title} icon={<BuildRounded/>}/>
            {loading && !unit ? (
                <Box sx={{pt: '6px'}}>
                    <SkeletonListRows variant="text"/>
                </Box>
            ) : (
                <Box sx={fadeInSx}>
                    <HeroEntity
                        name={unit.name}
                        subline={sourceInfo.label}
                        sublineHint={sourceInfo.isFallback ? sourceInfo.caption : null}
                        data-testid="service-unit-name"
                    />
                    {(unit.email || unit.phone) && (
                        <ContactChannelLinks email={unit.email} phone={unit.phone} level="bodySmall" sx={{mt: '8px'}} data-testid="service-unit-hotline"/>
                    )}
                    {serviceContacts.length > 0 && (
                        <Box sx={heroSectionSx}>
                            {collapsible ? (
                                <>
                                    <ButtonBase
                                        onClick={() => setContactsOpen((open) => !open)}
                                        aria-expanded={contactsOpen}
                                        data-testid="service-unit-contacts-toggle"
                                        sx={{display: 'flex', alignItems: 'center', gap: '2px', borderRadius: '4px'}}
                                    >
                                        <Typography level="bodySmall" sx={{color: '#777'}}>
                                            {t('{{count}} named contacts', {count: serviceContacts.length})}
                                        </Typography>
                                        <KeyboardArrowDownRounded sx={{
                                            fontSize: 18,
                                            color: '#777',
                                            transition: 'transform 150ms',
                                            transform: contactsOpen ? 'rotate(180deg)' : 'none',
                                        }}/>
                                    </ButtonBase>
                                    <Collapse in={contactsOpen}>
                                        <Box sx={{pt: '10px'}}>{contactList}</Box>
                                    </Collapse>
                                </>
                            ) : contactList}
                        </Box>
                    )}
                </Box>
            )}
        </WidgetBox>
    );
};

export default HeroServiceUnitCard;
